import api from '../../../config/axios';
import { API } from '../../../config/links';
import type { Change } from '../components/ChangeList';
import {
  LibraryAuditLogItem,
  LibraryCatalogEntry,
  LibraryDetail,
  LibraryEntry,
  LibraryRef,
  LibrarySummary,
  LibraryVersionItem,
  PreviewField,
} from './types';

const L = API.dynamicFieldLibrary;
const params = (ref: LibraryRef, extra?: Record<string, unknown>) => ({ sensor_type: ref.sensor_type, category: ref.category, ...extra });

export const dynamicFieldsApi = {
  libraries: async () => (await api.get<LibrarySummary[]>(L.libraries)).data,
  /** The draft if there is one, else the published version; null when the library does not exist yet. */
  current: async (ref: LibraryRef) => (await api.get<LibraryDetail | null>(L.library, { params: params(ref) })).data,
  saveDraft: async (ref: LibraryRef, fields: LibraryEntry[], note?: string) =>
    (await api.put<LibraryDetail>(L.library, { fields, note: note ?? null }, { params: params(ref) })).data,
  /** `acknowledgeImpact` confirms removing entries that published forms select (otherwise the API answers 409). */
  publish: async (ref: LibraryRef, note?: string, acknowledgeImpact = false) =>
    (await api.post<LibraryDetail>(L.publish, { note: note || null, acknowledge_impact: acknowledgeImpact }, { params: params(ref) })).data,
  discardDraft: async (ref: LibraryRef) => { await api.delete(L.draft, { params: params(ref) }); },
  versions: async (ref: LibraryRef) => (await api.get<LibraryVersionItem[]>(L.versions, { params: params(ref) })).data,
  version: async (ref: LibraryRef, version: number) => (await api.get<LibraryDetail>(L.version(version), { params: params(ref) })).data,
  versionDiff: async (ref: LibraryRef, version: number, against: 'live' | 'previous' = 'live') =>
    (await api.get<Change[]>(L.versionDiff(version), { params: params(ref, { against }) })).data,
  rollback: async (ref: LibraryRef, version: number, note?: string, acknowledgeImpact = false) =>
    (await api.post<LibraryDetail>(L.rollback(version), { note: note || null, acknowledge_impact: acknowledgeImpact }, { params: params(ref) })).data,
  auditLogs: async (ref: LibraryRef, page?: { limit?: number; offset?: number }) =>
    (await api.get<LibraryAuditLogItem[]>(L.auditLogs, { params: params(ref, page) })).data,
  /** The published library as a running form receives it; `range` decides the calibration point keys. */
  preview: async (ref: LibraryRef, range: string) =>
    (await api.get<{ fields: PreviewField[] }>(API.calibrationForms.calibrationDataSchema, { params: params(ref, { range: range.trim() || undefined }) })).data.fields,
  /** Published entries a form can select from. */
  catalog: async (ref: LibraryRef) => (await api.get<LibraryCatalogEntry[]>(L.entries, { params: params(ref) })).data,
};
